import React, { useState } from "react";

import { capitalize } from "lodash";
import { find, orderBy } from "lodash/fp";

import {
  Collapse,
  OutlinedInput,
  Typography,
  TypographyProps,
  styled,
} from "@mui/material";

import { useAuthenticated } from "@app/common/auth/useAuth";
import { EnterpriseWorkspaceUser } from "@app/common/workspaces/types";
import { useAdminWorkspace } from "@app/common/workspaces/useWorkspace";
import { isWorkspaceAdmin } from "@app/common/workspaces/workspaceUtils";

import Alert from "@rui/Alert";
import ConfirmationModal from "@rui/ConfirmationModal";
import Stack, { Row, StackProps } from "@rui/Stack";
import Switch from "@rui/Switch/Switch";

import SettingsTitle from "../../components/SettingsTitle";

const StyledText = styled((props: TypographyProps) => (
  <Typography variant="bodySecondaryLink" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.secondary[500],
  "&:hover": { cursor: "pointer" },
}));

const Disclaimer = styled((props: StackProps) => (
  <Stack spacing={1.5} {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.shades.ghost[300],
  padding: theme.spacing(1.5),
  borderRadius: "2px",
}));

const GlobalReportAccessModule = () => {
  const { workspace, updateWorkspaceUser } = useAdminWorkspace();
  const workspaceAdminsAndOwners = workspace.users.filter(isWorkspaceAdmin);
  const [open, setOpen] = useState(false);
  const [userEditing, setUserEditing] =
    useState<EnterpriseWorkspaceUser | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const { user } = useAuthenticated();
  const [nameInputValue, setNameInputValue] = useState<string>("");

  const sortedAdminsAndOwners = orderBy(
    "firstName",
    "asc",
    workspaceAdminsAndOwners
  );

  const handleToggleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    userId: string
  ) => {
    const { checked } = event.target;
    const userToEdit = find({ userId }, workspaceAdminsAndOwners)!;
    // if user is trying to turn on access, we want to show the confirmation dialog
    if (checked) {
      setUserEditing(userToEdit);
      setShowConfirmationDialog(true);
    } else {
      updateWorkspaceUser({
        userId: userToEdit.userId,
        globalSessionAccessEnabled: false,
      });
    }
  };
  const confirmDisabled = nameInputValue != user.fullName;

  const handleTextInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNameInputValue(event.target.value);
  };

  const handleClose = () => {
    setUserEditing(null);
    setShowConfirmationDialog(false);
    setNameInputValue("");
  };

  const handleConfirm = () => {
    updateWorkspaceUser({
      userId: userEditing!.userId,
      globalSessionAccessEnabled: true,
    });
    setNameInputValue("");
    setShowConfirmationDialog(false);
    setUserEditing(null);
  };

  return (
    <>
      <SettingsTitle
        title="Global Report Access"
        subtitle="When enabled, these users can view meeting reports for anyone in the workspace."
      />
      <ConfirmationModal
        size="md"
        onClose={handleClose}
        onConfirm={handleConfirm}
        open={showConfirmationDialog}
        confirmProps={{ disabled: confirmDisabled }}
      >
        <Typography variant="h2">Enable global report access?</Typography>
        <Typography
          variant="bodyPrimaryReg"
          sx={{ color: "shades.neutral.600" }}
        >
          When you enable global report access for {userEditing?.fullName} (
          {capitalize(userEditing?.role)}), they will be able to see reports for
          <Typography variant="bodyPrimaryMed">
            {" "}
            everyone in the workspace.
          </Typography>
        </Typography>
        <Disclaimer>
          <Typography variant="bodySecondaryReg">
            I, {user.fullName}, acknowledge the above, and confirm that I am
            authorized by my organization to grant global access to all reports
            in the Workspace.
          </Typography>
          <OutlinedInput
            placeholder="Type your name here"
            sx={{ backgroundColor: "white" }}
            onInput={handleTextInput}
            value={nameInputValue}
          />
        </Disclaimer>
      </ConfirmationModal>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={1.5}>
          <Alert severity="warning">
            This can only be enabled for Workspace Owners and Admins. For
            privacy, Read does not recommend enabling global report access, as
            it allows users to access any report in the Workspace. Proceed with
            caution.
          </Alert>
          <Stack>
            {sortedAdminsAndOwners.map((user) => (
              <Row key={user.userId} alignItems="center">
                <Switch
                  checked={user.globalSessionAccessEnabled ?? false}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleToggleChange(event, user.userId)
                  }
                />
                <Typography variant="bodyPrimaryReg">
                  {user.fullName} ({capitalize(user.role)})
                </Typography>
              </Row>
            ))}
          </Stack>
        </Stack>
      </Collapse>
      <StyledText onClick={() => setOpen(!open)}>
        {open ? "Hide" : "View"} permissions
      </StyledText>
    </>
  );
};

export default GlobalReportAccessModule;
